<script setup>
import { ref, onMounted } from "vue";
import { Field, ErrorMessage, useForm } from "vee-validate";
import * as yup from "yup";
import { defineEmits, defineExpose } from "vue";
import { phonePrefixes, getUserCountry } from "@/utils/countryUtils";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
library.add(faEye, faEyeSlash);
const selectedPrefix = ref(phonePrefixes[0].prefix);
const selectedCountryCode = ref(phonePrefixes[0].code);
const showPassword = ref(false);
const showPasswordConfirmation = ref(false);
const emit = defineEmits(["submit"]);
const validationSchema = yup.object({
  name: yup.string().required("El nombre es obligatorio"),
  email: yup
    .string()
    .email("Correo electrónico inválido")
    .required("El correo electrónico es obligatorio"),
  phone: yup.string().required("El número de teléfono es obligatorio"),
  shirt_size: yup.string().required("La talla de camisa es obligatoria"),
  birthdate: yup
    .date()
    .required("La fecha de nacimiento es obligatoria")
    .max(new Date(), "La fecha no puede ser futura")
    .min(new Date(1900, 0, 1), "Fecha inválida"),
  password: yup
    .string()
    .required("La contraseña es obligatoria")
    .min(8, "La contraseña debe tener al menos 8 caracteres"),
  passwordConfirmation: yup
    .string()
    .required("La confirmación de contraseña es obligatoria")
    .oneOf([yup.ref("password")], "Las contraseñas no coinciden"),
  terms: yup
    .boolean()
    .oneOf([true], "Debes aceptar los términos y condiciones"),
});
const {
  handleSubmit: validateAndSubmit,
  values,
  resetForm,
} = useForm({
  validationSchema,
});
const onSubmit = (values) => {
  const formData = {
    ...values,
    selectedCountryCode: selectedCountryCode.value,
    selectedPrefix: selectedPrefix.value,
  };
  emit("submit", formData);
};
const handleSubmit = validateAndSubmit(onSubmit);
const togglePassword = () => {
  showPassword.value = !showPassword.value;
};
const togglePasswordConfirmation = () => {
  showPasswordConfirmation.value = !showPasswordConfirmation.value;
};
const selectCountry = (code) => {
  selectedCountryCode.value = code;
  const country = phonePrefixes.find((c) => c.code === code);
  if (country) {
    selectedPrefix.value = country.prefix;
  }
};
onMounted(async () => {
  try {
    const country = await getUserCountry();
    selectedCountryCode.value = country.code;
    selectedPrefix.value = country.prefix;
  } catch (error) {
    console.error("Error al detectar el país:", error);
  }
});
defineExpose({
  resetForm,
  selectedPrefix,
  selectedCountryCode,
});
</script>
<template>
  <form
    @submit.prevent="handleSubmit"
    class="max-w-md mx-auto lg:p-6 space-y-6"
  >
    <Field
      name="name"
      type="text"
      class="w-full px-4 py-3 border rounded-lg bg-white text-black placeholder-gray-500 focus:ring-2 focus:ring-[#99b642]"
      placeholder="Ingresa tu nombre"
    />
    <ErrorMessage name="name" class="text-white text-sm" />
    <Field
      name="email"
      type="email"
      class="w-full px-4 py-3 border rounded-lg bg-white text-black placeholder-gray-500 focus:ring-2 focus:ring-[#99b642]"
      placeholder="Ingresa tu correo electrónico"
    />
    <ErrorMessage name="email" class="text-white text-sm" />
    <div class="flex space-x-2">
      <select
        v-model="selectedCountryCode"
        @change="selectCountry(selectedCountryCode)"
        class="p-3 border rounded-lg bg-white text-black placeholder-gray-500"
      >
        <option
          v-for="country in phonePrefixes"
          :key="country.code"
          :value="country.code"
        >
          {{ country.prefix }}
        </option>
      </select>
      <Field
        name="phone"
        type="tel"
        class="w-full px-4 py-3 border rounded-lg bg-white text-black placeholder-gray-500 focus:ring-2 focus:ring-[#99b642]"
        placeholder="Ingresa tu número"
      />
    </div>
    <ErrorMessage name="phone" class="text-white text-sm" />
    <div class="space-y-2">
      <label for="birthdate" class="block text-white text-sm"
        >Fecha de nacimiento</label
      >
      <Field
        name="birthdate"
        type="date"
        class="w-full px-4 py-3 border rounded-lg bg-white text-black placeholder-gray-500 focus:ring-2 focus:ring-[#99b642]"
      />
      <ErrorMessage name="birthdate" class="text-white text-sm" />
    </div>
    <Field
      name="shirt_size"
      as="select"
      class="w-full px-4 py-3 border rounded-lg bg-white text-black placeholder-gray-500 focus:ring-2 focus:ring-[#99b642]"
    >
      <option value="">Selecciona tu talla de camisa</option>
      <option value="XS">XS</option>
      <option value="S">S</option>
      <option value="M">M</option>
      <option value="L">L</option>
      <option value="XL">XL</option>
      <option value="XXL">XXL</option>
    </Field>
    <ErrorMessage name="shirt_size" class="text-white text-sm" />
    <div class="relative">
      <Field
        name="password"
        :type="showPassword ? 'text' : 'password'"
        class="w-full px-4 py-3 border rounded-lg bg-white text-black placeholder-gray-500 focus:ring-2 focus:ring-[#99b642]"
        placeholder="Contraseña"
      />
      <button
        type="button"
        @click="togglePassword"
        class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
      >
        <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'" />
      </button>
    </div>
    <ErrorMessage name="password" class="text-white text-sm" />
    <div class="relative">
      <Field
        name="passwordConfirmation"
        :type="showPasswordConfirmation ? 'text' : 'password'"
        class="w-full px-4 py-3 border rounded-lg bg-white text-black placeholder-gray-500 focus:ring-2 focus:ring-[#99b642]"
        placeholder="Confirmar contraseña"
      />
      <button
        type="button"
        @click="togglePasswordConfirmation"
        class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
      >
        <font-awesome-icon
          :icon="showPasswordConfirmation ? 'eye-slash' : 'eye'"
        />
      </button>
    </div>
    <ErrorMessage name="passwordConfirmation" class="text-white text-sm" />
    <div class="flex items-start mb-4">
      <Field
        name="terms"
        type="checkbox"
        class="h-5 w-5 rounded border-gray-300 text-[#99b642] focus:ring-[#99b642] mt-1"
      />
      <label for="terms" class="text-white ml-2 text-sm">
        Acepto los
        <a href="/terminos-y-condiciones" class="text-[#d4e4a9] underline"
          >Términos y condiciones</a
        >
      </label>
    </div>
    <ErrorMessage name="terms" class="text-white text-sm -mt-2" />
    <button
      type="submit"
      class="w-full py-3 bg-white text-black font-semibold rounded-lg shadow-md hover:bg-gray-200 focus:ring-4 focus:ring-[#99b642] focus:ring-opacity-50 transition-all"
    >
      <slot name="button-content">Registrarse</slot>
    </button>
  </form>
</template>
<style scoped>
form {
  animation: fadeIn 0.5s ease-out;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
